import { EventBus, globalEventBus } from './EventBus';
import { GameLoop } from './GameLoop';
import { InputManager } from './InputManager';
import { Renderer } from '@/rendering/Renderer';
import { Camera } from '@/rendering/Camera';
import { Lighting } from '@/rendering/Lighting';
import { PostProcessor } from '@/rendering/PostProcessor';
import { HexGrid } from '@/hex/HexGrid';
import { HexRenderer } from '@/hex/HexRenderer';
import { HexSelection } from '@/hex/HexSelection';
import { World } from '@/ecs/World';

export interface EngineConfig {
  canvas: HTMLCanvasElement;
}

/**
 * Main engine orchestrator
 * Initializes and coordinates all game systems
 */
export class Engine {
  // Core systems
  public readonly eventBus: EventBus;
  public readonly gameLoop: GameLoop;
  public readonly inputManager: InputManager;

  // Rendering
  public readonly renderer: Renderer;
  public readonly camera: Camera;
  public readonly lighting: Lighting;
  public readonly postProcessor: PostProcessor;

  // Hex grid
  public readonly hexGrid: HexGrid;
  public readonly hexRenderer: HexRenderer;
  public readonly hexSelection: HexSelection;

  // ECS
  public readonly world: World;

  private isInitialized = false;

  constructor(config: EngineConfig) {
    // Use global event bus for cross-system communication
    this.eventBus = globalEventBus;

    // Initialize core systems
    this.gameLoop = new GameLoop();
    this.inputManager = new InputManager(config.canvas, this.eventBus);

    // Initialize rendering
    this.renderer = new Renderer(config.canvas, this.eventBus);
    this.camera = new Camera(this.eventBus);
    this.lighting = new Lighting(this.renderer.scene);
    this.postProcessor = new PostProcessor(
      this.renderer.renderer,
      this.renderer.scene,
      this.camera.instance,
      this.eventBus
    );

    // Initialize hex grid
    this.hexGrid = new HexGrid();
    this.hexRenderer = new HexRenderer(this.renderer.scene, this.hexGrid);
    this.hexSelection = new HexSelection(
      this.camera.instance,
      this.hexRenderer,
      this.eventBus
    );

    // Initialize ECS
    this.world = new World(this.eventBus);

    this.setupGameLoop();
    this.isInitialized = true;
  }

  private setupGameLoop(): void {
    // Fixed update for game logic
    this.gameLoop.onUpdate((deltaTime) => {
      this.world.update(deltaTime);
    });

    // Render loop
    this.gameLoop.onRender((interpolation) => {
      this.world.render(interpolation);
      this.postProcessor.render();
    });
  }

  /**
   * Start the game engine
   */
  start(): void {
    if (!this.isInitialized) {
      throw new Error('Engine not initialized');
    }
    // Render one frame immediately
    this.postProcessor.render();
    console.log('Initial render complete');
    this.gameLoop.start();
  }

  /**
   * Stop the game engine
   */
  stop(): void {
    this.gameLoop.stop();
  }

  /**
   * Check if the engine is running
   */
  get running(): boolean {
    return this.gameLoop.running;
  }

  /**
   * Clean up all resources
   */
  dispose(): void {
    this.stop();
    this.inputManager.dispose();
    this.hexRenderer.dispose();
    this.postProcessor.dispose();
    this.renderer.dispose();
    this.world.clear();
    this.eventBus.clear();
  }
}
